// Вид от первого лица: 3D-модель оружия + low-poly руки в отдельном проходе
// рендера (Renderer.setViewModelInstances — своя ближняя плоскость, depth
// очищен: ствол не уходит в стену). Анимация процедурная: покачивание,
// ходьба, инерция взгляда, отдача, подъём при смене, перезарядка-«рывок»,
// удар кулаком, бросок гранаты, вибрация пилы.

import type { MeshInstance, Renderer } from "../engine/renderer";
import type { LoadedMesh } from "../engine/meshLoader";
import { WeaponId, weaponDef } from "./catalog";
import type { WeaponAssets } from "./assets";
import { ARM, FP_RIGS, type FpRig, type HandRig } from "./rig";
import {
  DEG, GLTF_TO_WORLD, m4, m4Basis, m4Euler, m4Mul, m4Point, m4Trs, type M4,
} from "./mat";

export interface FpCamera {
  x: number; y: number; z: number;
  yaw: number; pitch: number; roll: number;
}

/** Где в руке правое запястье — конец раструба краги (система руки, м),
 *  см. make_parts.py. */
const WRIST_R: [number, number, number] = [-0.105, -0.031, -0.004];
const WRIST_L: [number, number, number] = [-0.105, 0.031, -0.004];

const SWITCH_SEC = 0.3;

function expDecay(v: number, k: number, dt: number): number {
  return v * Math.exp(-k * dt);
}

export class ViewModel {
  private weaponInst: MeshInstance | null = null;
  private weaponMeshKey = "";
  private handR: MeshInstance | null = null;
  private handL: MeshInstance | null = null;
  private armR: MeshInstance | null = null;
  private armL: MeshInstance | null = null;
  private upR: MeshInstance | null = null;
  private upL: MeshInstance | null = null;
  private handFrame = m4();
  private flash: MeshInstance | null = null;
  private grenadeInHand: MeshInstance | null = null;
  private list: MeshInstance[] = [];

  private weapon: number = WeaponId.Rifle;
  private switchT = 1; // 0..1 подъём после смены
  private bobPhase = 0;
  private bobW = 0;
  private recoil = 0;  // м назад
  private kick = 0;    // рад вверх
  private swayYaw = 0;
  private swayPitch = 0;
  private lastYaw = 0;
  private lastPitch = 0;
  private hasLast = false;
  private flashUntil = 0;
  private flashRoll = 0;
  private actionT = 99; // секунды с последнего выстрела/удара
  private punchLeft = false;
  private reloadT = 99;
  private firingHeldT = 0;
  private timeSec = 0;

  /** Мировая точка дула (для вспышек/луча/пламени своего выстрела). */
  muzzle: [number, number, number] = [0, 0, 0];
  muzzleValid = false;
  /** Направление ствола (мир). */
  muzzleDir: [number, number, number] = [1, 0, 0];

  private cam = m4();
  private wpn = m4();
  private tmpA = m4();
  private tmpB = m4();

  constructor(private renderer: Renderer, private assets: WeaponAssets) {}

  /** Смена оружия в руках — анимация подъёма. */
  setWeapon(id: number): void {
    if (id === this.weapon) return;
    this.weapon = id;
    this.switchT = 0;
    this.actionT = 99;
    this.reloadT = 99;
  }

  /** Выстрел/удар/бросок: отдача и анимации действия. */
  onFire(): void {
    const def = weaponDef(this.weapon);
    const rig = FP_RIGS[this.weapon];
    this.recoil = Math.min(0.12, this.recoil + def.fp.recoil);
    this.kick = Math.min(0.35, this.kick + def.fp.kick);
    this.actionT = 0;
    if (this.weapon === WeaponId.Fists) this.punchLeft = !this.punchLeft;
    if (rig && rig.flash > 0) {
      this.flashUntil = this.timeSec + 0.05;
      this.flashRoll = Math.random() * Math.PI * 2;
    }
  }

  /** Добавились патроны — «перезарядка» (рывок оружия). */
  onReload(): void {
    if (this.weapon === WeaponId.Fists) return;
    this.reloadT = 0;
  }

  /** Огонь непрерывным оружием удерживается (пила/огнемёт) — вибрация. */
  setFiringHeld(on: boolean, dt: number): void {
    this.firingHeldT = on ? Math.min(1, this.firingHeldT + dt * 6) : Math.max(0, this.firingHeldT - dt * 4);
  }

  private inst(key: string, prev: MeshInstance | null): MeshInstance | null {
    const mesh: LoadedMesh | null = this.assets.get(key);
    if (!mesh) return prev;
    if (prev && prev.mesh === mesh) return prev;
    const inst = this.renderer.createMeshInstance({ mesh, x: 0, y: 0, z: 0, yaw: 0, scale: 1, initialAnim: "idle" });
    inst.worldMatrix = m4();
    return inst;
  }

  /** Кадр: собрать матрицы и список инстансов для прохода. visible=false — убрать. */
  update(visible: boolean, cam: FpCamera, speed: number, grounded: boolean, dt: number, ammo: number): void {
    this.timeSec += dt;
    if (!visible) {
      this.list.length = 0;
      this.renderer.setViewModelInstances(this.list);
      this.muzzleValid = false;
      this.hasLast = false;
      return;
    }
    const rig = FP_RIGS[this.weapon] ?? FP_RIGS[WeaponId.Rifle]!;
    const def = weaponDef(this.weapon);

    // ── инстансы ──
    this.handR = this.inst("hand_r", this.handR);
    this.handL = this.inst("hand_l", this.handL);
    this.armR = this.inst("forearm", this.armR);
    this.armL = this.inst("forearm", this.armL);
    this.upR = this.inst("forearm", this.upR);
    this.upL = this.inst("forearm", this.upL);
    this.flash = this.inst("muzzle_flash", this.flash);
    if (def.model && this.weaponMeshKey !== def.key) {
      const mesh = this.assets.get(def.key);
      if (mesh) {
        this.weaponInst = this.renderer.createMeshInstance({ mesh, x: 0, y: 0, z: 0, yaw: 0, scale: 1, initialAnim: "idle" });
        this.weaponInst.worldMatrix = m4();
        this.weaponMeshKey = def.key;
      }
    }
    if (this.weapon === WeaponId.Grenade) this.grenadeInHand = this.inst("grenade", this.grenadeInHand);

    // ── анимационные состояния ──
    this.switchT = Math.min(1, this.switchT + dt / SWITCH_SEC);
    this.actionT += dt;
    this.reloadT += dt;
    this.recoil = expDecay(this.recoil, 14, dt);
    this.kick = expDecay(this.kick, 10, dt);
    const wTarget = grounded ? Math.min(1, speed / 8) : 0;
    this.bobW += (wTarget - this.bobW) * Math.min(1, dt * 8);
    this.bobPhase += dt * (4 + 7 * this.bobW) * (speed > 0.3 ? 1 : 0.3);
    if (this.hasLast) {
      let dyaw = cam.yaw - this.lastYaw;
      if (dyaw > Math.PI) dyaw -= Math.PI * 2;
      if (dyaw < -Math.PI) dyaw += Math.PI * 2;
      const dp = cam.pitch - this.lastPitch;
      const k = Math.min(1, dt * 12);
      this.swayYaw += (Math.max(-0.07, Math.min(0.07, -dyaw * 0.9)) - this.swayYaw) * k;
      this.swayPitch += (Math.max(-0.06, Math.min(0.06, -dp * 0.9)) - this.swayPitch) * k;
    }
    this.lastYaw = cam.yaw;
    this.lastPitch = cam.pitch;
    this.hasLast = true;

    // ── камера ──
    const cy = Math.cos(cam.yaw), sy = Math.sin(cam.yaw);
    const cp = Math.cos(cam.pitch), sp = Math.sin(cam.pitch);
    const f = [cy * cp, sy * cp, sp];
    const cr = Math.cos(cam.roll), sr = Math.sin(cam.roll);
    const upW = cam.roll !== 0 ? [sy * cp * sr, -cy * cp * sr, cr] : [0, 0, 1];
    // right = f × up, up' = right × f, left = -right
    let rx = f[1]! * upW[2]! - f[2]! * upW[1]!;
    let ry = f[2]! * upW[0]! - f[0]! * upW[2]!;
    let rz = f[0]! * upW[1]! - f[1]! * upW[0]!;
    const rl = Math.hypot(rx, ry, rz) || 1;
    rx /= rl; ry /= rl; rz /= rl;
    const ux = ry * f[2]! - rz * f[1]!;
    const uy = rz * f[0]! - rx * f[2]!;
    const uz = rx * f[1]! - ry * f[0]!;
    m4Basis(this.cam, f, [-rx, -ry, -rz], [ux, uy, uz], [cam.x, cam.y, cam.z]);

    // ── смещения анимации (система камеры) ──
    const bob = this.bobW;
    const breathe = Math.sin(this.timeSec * 1.7) * 0.0025;
    let ox = -this.recoil;
    let oy = Math.sin(this.bobPhase) * 0.011 * bob;
    let oz = -Math.abs(Math.cos(this.bobPhase)) * 0.009 * bob + breathe;
    let pitch = -this.kick + this.swayPitch;
    let yaw = this.swayYaw;
    let roll = Math.sin(this.bobPhase) * 0.02 * bob;
    // Подъём после смены: снизу, наклонён стволом вниз.
    const sw = 1 - this.switchT;
    const swE = sw * sw * (3 - 2 * sw);
    oz -= 0.22 * swE;
    pitch += 0.6 * swE;
    // «Перезарядка»: рывок с наклоном.
    if (this.reloadT < 0.55) {
      const u = Math.sin((this.reloadT / 0.55) * Math.PI);
      roll += 0.45 * u;
      oz -= 0.05 * u;
      pitch += 0.15 * u;
    }
    // После выстрела ракетницы/снайперки — опускание (досылание).
    if ((this.weapon === WeaponId.Rocket || this.weapon === WeaponId.Sniper) && this.actionT > 0.25 && this.actionT < 0.9) {
      const u = Math.sin(((this.actionT - 0.25) / 0.65) * Math.PI);
      pitch += 0.22 * u;
      oz -= 0.04 * u;
      roll += 0.12 * u;
    }
    // Пила: вибрация (на холостых — слабая).
    if (this.weapon === WeaponId.Chainsaw) {
      const a = 0.0015 + 0.004 * this.firingHeldT;
      oy += (Math.random() - 0.5) * a;
      oz += (Math.random() - 0.5) * a;
      ox += 0.04 * this.firingHeldT;
    }
    // Огнемёт: лёгкая дрожь при огне.
    if (this.weapon === WeaponId.Flamethrower) {
      oz += (Math.random() - 0.5) * 0.002 * this.firingHeldT;
    }

    this.list.length = 0;

    // ── оружие ──
    const showWeapon = !!def.model && this.weaponInst && this.weaponMeshKey === def.key;
    let throwHide = false;
    if (this.weapon === WeaponId.Grenade) {
      // Бросок: замах назад-вверх, выброс вперёд; граната исчезает, пока
      // не «достанут» следующую.
      const t = this.actionT;
      if (t < 0.12) {
        const u = t / 0.12;
        ox -= 0.08 * u; oz += 0.07 * u; pitch += 0.5 * u;
      } else if (t < 0.32) {
        const u = (t - 0.12) / 0.2;
        ox += -0.08 + 0.2 * u; oz += 0.07 - 0.12 * u; pitch += 0.5 - 1.1 * u;
      } else if (t < 0.7) {
        const u = (t - 0.32) / 0.38;
        ox += 0.12 * (1 - u); oz -= 0.05 * (1 - u) + 0.15 * Math.sin(u * Math.PI); pitch -= 0.6 * (1 - u);
      }
      throwHide = t > 0.18 && t < 0.62;
      if (ammo === 0) throwHide = true;
    }

    m4Trs(this.tmpA, rig.pos[0] + ox, rig.pos[1] + oy, rig.pos[2] + oz,
      rig.rot[0] * DEG + roll, rig.rot[1] * DEG + pitch, rig.rot[2] * DEG + yaw, rig.scale);
    m4Mul(this.wpn, this.cam, this.tmpA);

    const pts = this.assets.pts(this.weapon);
    if (showWeapon && this.weaponInst) {
      m4Mul(this.weaponInst.worldMatrix!, this.wpn, GLTF_TO_WORLD);
      this.list.push(this.weaponInst);
    }
    if (this.weapon === WeaponId.Grenade && this.grenadeInHand && !throwHide) {
      m4Mul(this.grenadeInHand.worldMatrix!, this.wpn, GLTF_TO_WORLD);
      this.list.push(this.grenadeInHand);
    }
    if (this.weapon === WeaponId.Grenade && this.weaponInst) {
      // модель гранаты рисуется grenadeInHand
      const i = this.list.indexOf(this.weaponInst);
      if (i >= 0) this.list.splice(i, 1);
    }

    // Дуло (мир).
    const mz = pts ? pts.muzzle : [0.6, 0, 0.1];
    this.muzzle = m4Point(this.wpn, mz[0]!, mz[1]!, mz[2]!);
    const tip = m4Point(this.wpn, mz[0]! + 1, mz[1]!, mz[2]!);
    this.muzzleDir = [tip[0] - this.muzzle[0], tip[1] - this.muzzle[1], tip[2] - this.muzzle[2]];
    this.muzzleValid = true;

    // Огнемёт: язык пламени у сопла, пока жмут огонь (мерцает).
    if (this.flash && this.weapon === WeaponId.Flamethrower && this.firingHeldT > 0.15 && ammo !== 0) {
      const f = this.firingHeldT;
      m4Trs(this.tmpA, mz[0]!, mz[1]!, mz[2]!, Math.random() * Math.PI * 2, 0, 0, 1);
      const lx = (2.6 + Math.random() * 1.4) * f, lw = (1.5 + Math.random() * 0.5) * f;
      for (let i = 0; i < 3; i++) {
        this.tmpA[i] = this.tmpA[i]! * lx;
        this.tmpA[4 + i] = this.tmpA[4 + i]! * lw;
        this.tmpA[8 + i] = this.tmpA[8 + i]! * lw;
      }
      m4Mul(this.tmpB, this.wpn, this.tmpA);
      m4Mul(this.flash.worldMatrix!, this.tmpB, GLTF_TO_WORLD);
      const k = 0.8 + Math.random() * 0.4;
      this.flash.emissive = [3.4 * k, 1.5 * k, 0.35 * k];
      this.list.push(this.flash);
    } else
    // Вспышка у дула.
    if (this.flash && rig.flash > 0 && this.timeSec < this.flashUntil) {
      m4Trs(this.tmpA, mz[0]!, mz[1]!, mz[2]!, this.flashRoll, 0, 0, rig.flash);
      m4Mul(this.tmpB, this.wpn, this.tmpA);
      m4Mul(this.flash.worldMatrix!, this.tmpB, GLTF_TO_WORLD);
      this.flash.emissive = this.weapon === WeaponId.Laser ? [3.0, 0.4, 0.3] : [3.2, 2.2, 0.9];
      this.list.push(this.flash);
    }

    // ── руки ──
    this.placeHand(this.handR, this.armR, this.upR, rig.handR, rig, pts, true);
    this.placeHand(this.handL, this.armL, this.upL, rig.handL, rig, pts, false);

    this.renderer.setViewModelInstances(this.list);
  }

  private placeHand(hand: MeshInstance | null, arm: MeshInstance | null, upper: MeshInstance | null, h: HandRig | null,
    rig: FpRig, pts: ReturnType<WeaponAssets["pts"]>, right: boolean): void {
    if (!hand || !h) return;
    const frame = this.tmpB;
    let base: M4;
    let px = h.off[0], py = h.off[1], pz = h.off[2];
    if (h.at === "cam") {
      base = this.cam;
      // Кулаки: удар одной рукой, вторая на месте; ходьба/отдача — как у оружия.
      if (this.weapon === WeaponId.Fists) {
        const mine = this.punchLeft === !right;
        if (mine && this.actionT < 0.3) {
          const u = Math.sin((this.actionT / 0.3) * Math.PI);
          px += 0.2 * u;
          py += (right ? 0.07 : -0.07) * u;
          pz += 0.04 * u;
        }
        px += Math.sin(this.bobPhase + (right ? 0 : Math.PI)) * 0.008 * this.bobW;
        pz += -Math.abs(Math.cos(this.bobPhase)) * 0.01 * this.bobW + (1 - this.switchT) * -0.2;
      }
    } else {
      base = this.wpn;
      const g = h.at === "grip_l" ? (pts ? pts.grip_l : [0.35, 0, 0.05]) : [0, 0, 0];
      px += g[0]!; py += g[1]!; pz += g[2]!;
      // Дробовик: помпа левой рукой после выстрела.
      if (this.weapon === WeaponId.Shotgun && h.at === "grip_l" && this.actionT > 0.15 && this.actionT < 0.55) {
        px -= 0.08 * Math.sin(((this.actionT - 0.15) / 0.4) * Math.PI);
      }
    }
    // Поворот шагами вокруг осей.
    m4Trs(frame, px, py, pz, 0, 0, 0, 1);
    for (const [ax, deg] of h.steps) {
      const r = deg * DEG;
      m4Euler(this.tmpA, ax === "x" ? r : 0, ax === "y" ? r : 0, ax === "z" ? r : 0);
      // frame = frame · R  (поворот в локальной системе руки, по очереди)
      m4Mul(frame, frame, this.tmpA);
    }
    const handFrame = this.handFrame;
    m4Mul(handFrame, base, frame);
    m4Mul(hand.worldMatrix!, handFrame, GLTF_TO_WORLD);
    this.list.push(hand);

    // Рука: запястье → локоть → плечо (двухзвенная IK, локоть уводится
    // вниз и наружу). Плечо — за кадром, у края экрана.
    if (!arm) return;
    const w = right ? WRIST_R : WRIST_L;
    const W = m4Point(handFrame, w[0], w[1], w[2]);
    const sa = right ? rig.elbowR : rig.elbowL; // «плечо» (система камеры)
    const S = m4Point(this.cam, sa[0], sa[1], sa[2]);
    const pole = right ? ARM.poleR : (rig.poleL ?? ARM.poleL);
    const P = [
      this.cam[0]! * pole[0]! + this.cam[4]! * pole[1]! + this.cam[8]! * pole[2]!,
      this.cam[1]! * pole[0]! + this.cam[5]! * pole[1]! + this.cam[9]! * pole[2]!,
      this.cam[2]! * pole[0]! + this.cam[6]! * pole[1]! + this.cam[10]! * pole[2]!,
    ];
    const a = ARM.upperarm, b = ARM.forearm;
    let ux = W[0] - S[0], uy = W[1] - S[1], uz = W[2] - S[2];
    let d = Math.hypot(ux, uy, uz) || 1e-3;
    ux /= d; uy /= d; uz /= d;
    d = Math.min(d, a + b - 1e-3);
    const cosA = Math.max(-1, Math.min(1, (a * a + d * d - b * b) / (2 * a * d)));
    const sinA = Math.sqrt(1 - cosA * cosA);
    // v — пол, ортогональный оси плечо→запястье.
    const pd = P[0]! * ux + P[1]! * uy + P[2]! * uz;
    let vx = P[0]! - ux * pd, vy = P[1]! - uy * pd, vz = P[2]! - uz * pd;
    const vl = Math.hypot(vx, vy, vz) || 1;
    vx /= vl; vy /= vl; vz /= vl;
    const E = [
      S[0] + a * (cosA * ux + sinA * vx),
      S[1] + a * (cosA * uy + sinA * vy),
      S[2] + a * (cosA * uz + sinA * vz),
    ];
    this.limb(arm, W, E, 0.85);
    this.list.push(arm);
    if (upper) {
      this.limb(upper, E, S, 1.05);
      this.list.push(upper);
    }
  }

  /** Рукав от точки A к точке B (ось +X модели), толщина × k. */
  private limb(inst: MeshInstance, A: number[], B: number[], k: number): void {
    let dx = B[0]! - A[0]!, dy = B[1]! - A[1]!, dz = B[2]! - A[2]!;
    const len = Math.hypot(dx, dy, dz) || 1e-3;
    dx /= len; dy /= len; dz /= len;
    let qx = this.cam[8]!, qy = this.cam[9]!, qz = this.cam[10]!;
    const d = qx * dx + qy * dy + qz * dz;
    qx -= dx * d; qy -= dy * d; qz -= dz * d;
    const ql = Math.hypot(qx, qy, qz) || 1;
    qx /= ql; qy /= ql; qz /= ql;
    const yx = qy * dz - qz * dy, yy = qz * dx - qx * dz, yz = qx * dy - qy * dx;
    m4Basis(this.tmpA, [dx * len, dy * len, dz * len], [yx * k, yy * k, yz * k], [qx * k, qy * k, qz * k], A);
    m4Mul(inst.worldMatrix!, this.tmpA, GLTF_TO_WORLD);
  }
}
